import React from 'react';
import useAlertStore, { Alert } from '../store/useAlertStore';
import styles from './GlobalAlert.module.css';

// Map alert types to custom CSS classes instead of Bootstrap classes
const alertClassMap = {
  success: styles.alertSuccess,
  error: styles.alertError,
  warning: styles.alertWarning,
  info: styles.alertInfo,
};

// Icons for different alert types
const AlertIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'success':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    case 'error':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M15 9L9 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    case 'warning':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10.29 3.86L1.82 18C1.64537 18.3024 1.55296 18.6453 1.55199 18.9945C1.55101 19.3437 1.6415 19.6871 1.81442 19.9905C1.98734 20.2939 2.23672 20.5467 2.53771 20.7238C2.83869 20.9009 3.1808 20.9961 3.53 21H21.47C21.8192 20.9961 22.1613 20.9009 22.4623 20.7238C22.7633 20.5467 23.0127 20.2939 23.1856 19.9905C23.3585 19.6871 23.449 19.3437 23.448 18.9945C23.447 18.6453 23.3546 18.3024 23.18 18L14.71 3.86C14.5317 3.56611 14.2807 3.32312 13.9812 3.15448C13.6817 2.98585 13.3437 2.89725 13 2.89725C12.6563 2.89725 12.3183 2.98585 12.0188 3.15448C11.7193 3.32312 11.4683 3.56611 11.29 3.86V3.86Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 9V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    default: // info
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
  }
};

// Individual alert component
const AlertItem = ({ alert }: { alert: Alert }) => {
  const hideAlert = useAlertStore(state => state.hideAlert);
  const alertClass = alertClassMap[alert.type] || alertClassMap.info;
  const visibilityClass = alert.isVisible ? styles.visible : styles.hidden;

  return (
    <div
      className={`alert alert-dismissible fade show ${styles.alertItem} ${alertClass} ${visibilityClass}`}
      role="alert"
      style={{ animationDelay: `${parseInt(alert.id) % 3 * 100}ms` }}
    >
      <div className="d-flex align-items-center gap-2">
        <AlertIcon type={alert.type} />
        <div className="flex-grow-1">
          <div className="fw-semibold">{alert.message}</div>
          {alert.details && alert.details.length > 0 && (
            <ul className="mb-0 mt-1 small">
              {alert.details.map((detail, index) => (
                <li key={index}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="alert"
          aria-label="Close"
          onClick={() => hideAlert(alert.id)}
        ></button>
      </div>
    </div>
  );
};

const GlobalAlert: React.FC = () => {
  const alerts = useAlertStore(state => state.alerts);

  if (alerts.length === 0) return null;

  return (
    <div className={styles.alertContainer}>
      {alerts.map(alert => (
        <AlertItem key={alert.id} alert={alert} />
      ))}
    </div>
  );
};

export default GlobalAlert; 